import React, { useEffect, useState } from "react";
import { classNames, uniqueUUID } from "../utils";

import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import Input from "../shared-components/Input";
import AddButton from "../shared-components/AddButton";
import { XIcon } from "../shared-components/icons";

export interface AddressesDefinitions {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  key: string;
}

type RowProps = {
  onChange?: (aliases: AddressesDefinitions) => void;
  error?: any;
  prefill?: AddressesDefinitions;
  isDirty?: boolean;
  validateFieldsRow?: (fields: AddressesDefinitions) => boolean;
  handleRemove: (idx: number) => void;
  idx: number;
};

function buildEmptyDefinition(): AddressesDefinitions {
  return {
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    key: uniqueUUID(),
  };
}

function ReportsWizardAddressFields({
  onChange,
  prefill,
  buildEmptyDefinition,
  fieldKeys,
  buttonText,
  skipInitialFill,
  isDirty,
  isValid,
  validateFieldsRow,
}: {
  onChange?: (fields: AddressesDefinitions[]) => void;
  prefill?: AddressesDefinitions[];
  alternateColor?: boolean;
  buildEmptyDefinition: () => AddressesDefinitions;
  fieldKeys: {
    key: string & keyof AddressesDefinitions;
    label: string;
    type?: string;
  }[];
  buttonText: string;
  label: string;
  skipInitialFill?: boolean;
  isDirty?: boolean;
  isValid?: boolean;
  validateFieldsRow?: (fields: AddressesDefinitions) => boolean;
}) {
  const [fieldsList, setFields] = useState<AddressesDefinitions[]>(
    skipInitialFill
      ? prefill || []
      : prefill
      ? [...prefill]
      : [buildEmptyDefinition()]
  );

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (idx: number, field: string, value: string) => {
    fieldsList[idx][field as keyof AddressesDefinitions] =
      value as AddressesDefinitions[keyof AddressesDefinitions];
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: AddressesDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (idx: number) => {
    removeField(idx);
  };

  return (
    <div className="flex flex-col">
      <div className="flex-1">
        {fieldsList.map((fields, idx) => (
          <div className="flex flex-wrap items-center">
            <PersonFormAddressRow
              prefill={fields}
              isDirty={isDirty}
              onChange={(e) => {
                const keys = Object.keys(e);
                keys.forEach((key) => {
                  updateField(idx, key, e[key as keyof AddressesDefinitions]);
                });
              }}
              validateFieldsRow={validateFieldsRow}
              handleRemove={handleRemove}
              idx={idx}
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <AddButton text={buttonText} onClick={handleAdd} />
      </div>
    </div>
  );
}

const PersonFormAddressRow = ({
  onChange,
  error,
  prefill,
  isDirty,
  validateFieldsRow,
  handleRemove,
  idx,
}: RowProps) => {
  const [libraries] = useState<["places"]>(["places"]);
  useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GTM_KEY || "",
    libraries,
  });

  const {
    ready,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 });

  const [fieldsList, setFields] = useState<AddressesDefinitions>(
    buildEmptyDefinition()
  );
  const updateField = (
    field: "street" | "city" | "state" | "zip" | "country",
    value: string
  ) => {
    fieldsList[field] = value;
    handleChange(fieldsList);
  };

  const handleChange = (list: AddressesDefinitions) => {
    setFields({ ...list });
    clearSuggestions();
    onChange?.(list);
  };

  useEffect(() => {
    if (prefill) {
      handleChange(prefill);
      let addressComponents = [
        prefill.street,
        prefill.city,
        prefill.state,
        prefill.zip,
        prefill.country,
      ];
      let _addressString = addressComponents.filter(Boolean).join(", ");
      setValue(_addressString, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleSelect = async (address: string) => {
    setValue(address, false);
    const results = await getGeocode({ address });
    let street: string = "";
    let street_number: string = "";
    let route: string = "";

    let components = results[0].address_components;
    components.forEach((component) => {
      if (component.types.includes("street_number")) {
        street_number = component.long_name;
      } else if (component.types.includes("route")) {
        route = component.long_name;
      }
      if (component.types.includes("locality")) {
        updateField("city", component.long_name);
      } else if (component.types.includes("administrative_area_level_1")) {
        updateField("state", component.short_name);
      } else if (component.types.includes("postal_code")) {
        updateField("zip", component.short_name);
      } else if (component.types.includes("country")) {
        updateField("country", component.short_name);
      }

      if (street_number && route) {
        street = `${street_number} ${route}`;
      } else if (route) {
        street = route;
      } else if (street_number) {
        street = street_number;
      }
      updateField("street", street);
    });
    clearSuggestions();
  };
  return (
    <>
      <div key={fieldsList.key} className="flex flex-wrap">
        <Combobox onSelect={(e) => handleSelect(e)}>
          <label
            htmlFor={`street-name`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            Street Address
          </label>
          <ComboboxInput
            onChange={(e) => {
              setValue(e.target.value);
              updateField("street", e.target.value);
            }}
            disabled={!ready}
            name={`street-name`}
            value={fieldsList.street}
            placeholder={""}
            autoComplete="off"
            style={{ width: "100%" }}
            className={classNames(
              isDirty && !validateFieldsRow?.(fieldsList)
                ? "border-red-500"
                : "",
              "border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-full"
            )}
          />
          <ComboboxPopover>
            <ComboboxList>
              {status === "OK" &&
                data.map((data) => {
                  return (
                    <ComboboxOption
                      key={data.place_id}
                      value={data.description}
                    />
                  );
                })}
            </ComboboxList>
          </ComboboxPopover>
        </Combobox>
        <div className="ml-4 mr-4 flex-1">
          <label
            htmlFor={`city`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            City/Village
          </label>
          <Input
            name={"city"}
            value={fieldsList.city}
            className={classNames(
              isDirty && !validateFieldsRow?.(fieldsList)
                ? "border-red-500"
                : "",
              "w-full"
            )}
            onChange={(event) => updateField("city", event.target.value)}
          ></Input>
        </div>
        <div className="mr-4  flex-1">
          <label
            htmlFor={`state`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            State/Province
          </label>
          <Input
            name={"state"}
            value={fieldsList.state}
            onChange={(event) => updateField("state", event.target.value)}
            className={classNames(
              isDirty && !validateFieldsRow?.(fieldsList)
                ? "border-red-500"
                : "",
              "w-full"
            )}
          ></Input>
        </div>

        <div className="mr-4  flex-1">
          <label
            htmlFor={`postal-code`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            Postal Code
          </label>
          <Input
            name={"postal-code"}
            value={fieldsList.zip}
            className={classNames(
              isDirty && !validateFieldsRow?.(fieldsList)
                ? "border-red-500"
                : "",
              "w-full"
            )}
            onChange={(event) => updateField("zip", event.target.value)}
          ></Input>
        </div>
        <div className="mr-4  flex-1 flex">
          {/* this div prevents the "XIcon" from ever wrapping by itself onto the next line */}
          {/* mr-4 prevents it from being drawn outside the parent's background */}
          <div className="relative mr-4">
            <label
              htmlFor={`country`}
              className={"block text-sm font-medium text-gray-600 mb-2"}
            >
              Country
            </label>
            <Input
              name={"country"}
              value={fieldsList.country}
              onChange={(event) => updateField("country", event.target.value)}
              className={classNames(
                isDirty && !validateFieldsRow?.(fieldsList)
                  ? "border-red-500"
                  : "",
                "w-full"
              )}
            ></Input>
            <div
              className="absolute -right-10 bottom-2 rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
              onClick={(e) => handleRemove(idx)}
            >
              <XIcon />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportsWizardAddressFields;
